import React, { useState, useMemo, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { arrayOf } from 'prop-types';
import { propTypes } from '../../util/types';
import { FormattedMessage } from '../../util/reactIntl';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { NamedLink } from '../../components';
import { Popover } from '@mui/material';

import css from './BookingCalendar.module.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

const EventBubble = React.forwardRef(({ event, onClick }, ref) => (
  <div ref={ref} className={css.eventBubble} onClick={onClick}>
    <span className={css.eventTitle}>{event.title}</span>
  </div>
));

EventBubble.displayName = 'EventBubble';

const EventMenu = ({ event, anchorEl, onClose }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
  >
    <div className={css.eventMenu}>
      <h3 className={css.eventMenuTitle}>{event.title}</h3>
      <p className={css.eventMenuDetail}>Service: {event.serviceName}</p>
      <p className={css.eventMenuDetail}>
        Time: {moment(event.start).format('LT')} - {moment(event.end).format('LT')}
      </p>
      <NamedLink
        name="SaleDetailsPage"
        params={{ id: event.transactionId }}
        className={css.viewTransactionButton}
      >
        View Booking
      </NamedLink>
    </div>
  </Popover>
);

const BookingCalendar = props => {
  const { transactions, userType, currentUser } = props;
  const [view, setView] = useState('month'); // 'month' or 'week'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const eventRefs = useRef({});

  const isBookingRelevant = useCallback(
    tx => {
      const isStylist = userType === 'stylist';
      const isCanceled = tx.attributes.transitions.some(
        trn => trn.transition === 'transition/cancel'
      );
      const isAccepted =
        tx.attributes.transitions.some(trn => trn.transition === 'transition/accept') &&
        !isCanceled;
      const isPending = tx.attributes.lastTransition === 'transition/confirm-payment';
      const isPast = isAccepted && tx.booking.attributes.start < new Date();
      const bookingStart = new Date(tx.booking.attributes.start);
      const now = new Date();

      if (isStylist) {
        return (
          (isPast || isAccepted || (isPending && bookingStart > now)) &&
          tx.provider.id.uuid === currentUser.id.uuid
        );
      }
      return false;
    },
    [userType, currentUser.id.uuid]
  );

  const relevantTransactions = useMemo(() => transactions.filter(isBookingRelevant), [
    transactions,
    isBookingRelevant,
  ]);

  const events = useMemo(() => {
    return relevantTransactions.map(tx => {
      const bookingStart = new Date(tx.booking.attributes.start);
      const bookingEnd = new Date(tx.booking.attributes.end);

      return {
        id: tx.id.uuid,
        title: tx.customer.attributes.profile.displayName,
        start: bookingStart,
        end: bookingEnd,
        serviceName: tx.attributes.protectedData.serviceName,
        transactionId: tx.id.uuid,
        isPending: tx.attributes.lastTransition === 'transition/confirm-payment',
      };
    });
  }, [relevantTransactions]);

  const handleSelectEvent = useCallback(event => {
    setSelectedEvent(event);
    setAnchorEl(eventRefs.current[event.id]);
  }, []);

  const closeEventMenu = useCallback(() => {
    setSelectedEvent(null);
    setAnchorEl(null);
  }, []);

  const getEventStyle = useCallback((event, start) => {
    const style = {
      backgroundColor: event.isPending ? '#FFC107' : start < new Date() ? '#e25e38' : '#4CAF50',
      color: 'white',
    };
    return { style };
  }, []);

  const EventComponent = useCallback(
    ({ event }) => (
      <EventBubble
        ref={el => (eventRefs.current[event.id] = el)}
        event={event}
        onClick={() => handleSelectEvent(event)}
      />
    ),
    [handleSelectEvent]
  );

  return (
    <div className={css.calendarContainer}>
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%', width: '100%' }}
        onView={setView}
        eventPropGetter={getEventStyle}
        views={['month', 'week']}
        defaultView={view}
        selectable={false}
        formats={{
          eventTimeRangeFormat: () => {
            return '';
          },
        }}
        components={{
          event: EventComponent,
        }}
      />
      {selectedEvent && (
        <EventMenu event={selectedEvent} anchorEl={anchorEl} onClose={closeEventMenu} />
      )}
    </div>
  );
};

BookingCalendar.propTypes = {
  transactions: arrayOf(propTypes.transaction).isRequired,
  userType: PropTypes.oneOf(['stylist', 'customer']).isRequired,
  currentUser: propTypes.currentUser.isRequired,
};

export default BookingCalendar;
